import type { 
  ServiceRequestDto, 
  ServiceType 
} from '../types/api/service';

/**
 * Content Service API Helpers - Convert content operations to ServiceRequestDto format
 * 
 * These functions convert content operations into the common ServiceRequestDto
 * format that can be processed by ApiService.processServiceRequest()
 */

// Request type constants for content operations
export const SYNC_REQUEST_TYPES = {
  // Content management
  CONTENT_GET: 'CONTENT_GET',
  CONTENT_UPDATE: 'CONTENT_UPDATE',
  CONTENT_LIST: 'CONTENT_LIST',
  CONTENT_SYNC: 'CONTENT_SYNC',
} as const;

// Service type mappings for content operations
const CONTENT_SERVICE_MAPPINGS: Record<string, ServiceType> = {
  // Content operations go to content service
  [SYNC_REQUEST_TYPES.CONTENT_GET]: 'content',
  [SYNC_REQUEST_TYPES.CONTENT_UPDATE]: 'content',
  [SYNC_REQUEST_TYPES.CONTENT_LIST]: 'content',
  [SYNC_REQUEST_TYPES.CONTENT_SYNC]: 'content',
};

/**
 * Create a ServiceRequestDto from operation data
 */
function createServiceRequest(requestType: string, payload: any): ServiceRequestDto {
  return {
    RequestType: requestType,
    Payload: JSON.stringify(payload)
  };
}

/**
 * Get the appropriate service type for a request
 */
export function getServiceTypeForRequest(requestType: string): ServiceType {
  return CONTENT_SERVICE_MAPPINGS[requestType] || 'content';
}

/**
 * Parse ServiceResponseDto payload back to typed object
 */
export function parseServiceResponse<T = any>(payload: string): T {
  try {
    return JSON.parse(payload);
  } catch (error) {
    throw new Error(`Failed to parse service response payload: ${error}`);
  }
}

// Content Management Helper Functions

/**
 * Create content get request
 */
export function createContentGetRequest(
  contentType: string,
  contentTypeId: string,
  languageTypeId: string
): ServiceRequestDto {
  return createServiceRequest(SYNC_REQUEST_TYPES.CONTENT_GET, {
    ContentType: contentType,
    ContentTypeId: contentTypeId,
    ContentLanguageTypeId: languageTypeId,
  });
}

/**
 * Create content update request
 */
export function createContentUpdateRequest(
  contentType: string,
  contentTypeId: string,
  languageTypeId: string,
  tableData: any
): ServiceRequestDto {
  return createServiceRequest(SYNC_REQUEST_TYPES.CONTENT_UPDATE, {
    ContentType: contentType,
    ContentTypeId: contentTypeId,
    ContentLanguageTypeId: languageTypeId,
    TableData: tableData,
  });
}

/**
 * Create content list request for a specific type
 */
export function createContentListRequest(
  contentType: string,
  languageTypeId?: string
): ServiceRequestDto {
  const payload: any = { ContentType: contentType };
  if (languageTypeId) {
    payload.ContentLanguageTypeId = languageTypeId;
  }
  return createServiceRequest(SYNC_REQUEST_TYPES.CONTENT_LIST, payload);
}

/**
 * Create content sync request to refresh content from backend
 */
export function createContentSyncRequest(
  contentTypes: string[],
  languageTypeId: string,
  forceSync: boolean = false
): ServiceRequestDto {
  return createServiceRequest(SYNC_REQUEST_TYPES.CONTENT_SYNC, {
    ContentTypes: contentTypes,
    ContentLanguageTypeId: languageTypeId,
    ForceSync: forceSync,
  });
}